/**
 * <HrInitiateLink /> — the "Initiate on behalf of employee" CTA
 * (M30.5.7).
 *
 * Renders a secondary-style link button that deep-links from the
 * existing employee-initiated transaction list pages (Leave / Loan /
 * Excuse / Attendance) to the M30 HR-initiate form at
 * /operations/transactions/{transactionType}/new.
 *
 * Use case: HR is sitting on /operations/leave/requests reviewing
 * employee-submitted leave; they realise they need to file leave
 * ON BEHALF of an employee who can't submit themselves (deceased,
 * absconded, system correction, etc.) — this is the signpost to
 * the M30 HR-initiate flow without having to navigate to the hub.
 *
 * The visual treatment is intentionally secondary (white background,
 * border, brand text) — the primary "New request" button on each
 * page is the employee-initiated path; this button is the EXCEPTION
 * path per the M30 doc's "agent handles 80%, HR handles the 20%
 * exception" framing.
 *
 * Why a primitive instead of inline buttons? Six list pages will
 * eventually consume this (Leave / Loan / Excuse / Attendance /
 * Overtime — when split out — / EOS). One source of truth for
 * styling, route mapping, and the i18n key.
 */

import { UserPlus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import type { TransactionType } from "../../lib/ai-tier-registry";

// Mirrors the route map in OperationsHubPage. Single source of truth
// would be a shared module; for now the duplication is 7 lines and
// changes in lockstep with the App.tsx route declarations.
const HR_INITIATE_ROUTE_BY_TYPE: Record<TransactionType, string> = {
  leave: "/operations/transactions/leave/new",
  loan: "/operations/transactions/loan/new",
  excuse: "/operations/transactions/excuse/new",
  attendance: "/operations/transactions/attendance/new",
  overtime: "/operations/transactions/overtime/new",
  eos: "/operations/transactions/eos/new",
  contract_amendment: "/operations/transactions/contract-amendment/new",
};

export interface HrInitiateLinkProps {
  transactionType: TransactionType;
  /** Optional label override. Defaults to the bilingual i18n key. */
  label?: string;
  /** Hide the icon (useful in tight rows). */
  hideIcon?: boolean;
  className?: string;
}

export function HrInitiateLink({
  transactionType,
  label,
  hideIcon,
  className,
}: HrInitiateLinkProps): React.ReactElement {
  const { t } = useTranslation();
  const to = HR_INITIATE_ROUTE_BY_TYPE[transactionType];
  return (
    <Link
      to={to}
      data-testid={`hr-initiate-link-${transactionType}`}
      className={
        className ??
        "inline-flex items-center gap-1 rounded-sm border border-brand bg-surface-default px-3 py-2 text-sm font-medium text-brand hover:bg-brand-bg/40"
      }
    >
      {!hideIcon && <UserPlus size={14} />}
      {label ?? t("operations.hr_initiate_on_behalf")}
    </Link>
  );
}
